import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ALL_MATCHUP_GAME_TYPE_BUCKETS,
  DEFAULT_MATCHUP_GAME_TYPE_BUCKETS,
  MATCHUP_DATE_PRESETS,
  MATCHUP_GAME_TYPE_BUCKETS,
  isAllGameTypeBucketsSelected,
  isDefaultBalancedBuckets,
} from '../../utils/matchupFilters';

function FilterField({ id, label, labelShort, className = '', children }) {
  return (
    <div className={`min-w-0 ${className}`.trim()}>
      <label htmlFor={id} className="block text-xs font-medium text-text-secondary mb-1">
        <span className="sm:hidden">{labelShort ?? label}</span>
        <span className="hidden sm:inline">{label}</span>
      </label>
      {children}
    </div>
  );
}

function formatSeasonLabel(season) {
  if (!season?.name) return '';
  const year = season.start_date ? new Date(season.start_date).getFullYear() : null;
  return year ? `${season.name} (${year})` : season.name;
}

function formatFilterDate(ymd) {
  if (!ymd) return '';
  const date = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(date.getTime())) return ymd;
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function summarizeSelected(options, selectedValues, { emptyLabel, allLabel, oneExtra = 'more' }) {
  if (!selectedValues?.length) return emptyLabel;
  if (selectedValues.length === options.length && allLabel) return allLabel;
  const labels = selectedValues
    .map((value) => options.find((option) => String(option.value) === String(value))?.label)
    .filter(Boolean);
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} + ${labels[1]}`;
  if (labels.length > 2) return `${labels[0]} + ${labels.length - 1} ${oneExtra}`;
  return emptyLabel;
}

function FilterMultiSelect({
  id,
  options,
  selectedValues,
  onChange,
  emptyLabel,
  allSelectedLabel,
  selectedLabel = null,
  groups = null,
  footer = null,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedSet = useMemo(
    () => new Set(selectedValues.map((value) => String(value))),
    [selectedValues]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleValue = (value) => {
    const key = String(value);
    if (selectedSet.has(key)) {
      onChange(selectedValues.filter((current) => String(current) !== key));
      return;
    }
    onChange([...selectedValues, value]);
  };

  const summary =
    selectedLabel ||
    summarizeSelected(options, selectedValues, {
      emptyLabel,
      allLabel: allSelectedLabel,
    });

  const renderOption = (option) => {
    const checked = selectedSet.has(String(option.value));
    const optionId = `${id}-${option.value}`;
    return (
      <label
        key={option.value}
        htmlFor={optionId}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-surface-subtle cursor-pointer"
      >
        <input
          id={optionId}
          type="checkbox"
          checked={checked}
          onChange={() => toggleValue(option.value)}
          className="rounded border-border-medium text-primary focus:ring-primary"
        />
        <span className="text-sm text-text-primary">{option.label}</span>
      </label>
    );
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        className="form-input w-full min-w-0 text-sm py-1.5 sm:py-2 text-left flex items-center justify-between gap-2"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="truncate">{summary}</span>
        <svg
          className={`w-4 h-4 shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open ? (
        <div
          className="absolute z-30 mt-1 w-full sm:w-72 rounded-lg border border-border-light bg-surface shadow-lg"
          role="listbox"
          aria-multiselectable="true"
        >
          <div className="max-h-64 overflow-y-auto py-1">
            {groups
              ? groups.map((group) => (
                  <div key={group.id} className="px-1 pb-1">
                    <p className="px-2.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                      {group.label}
                    </p>
                    {options.filter((option) => option.group === group.id).map(renderOption)}
                  </div>
                ))
              : options.map(renderOption)}
          </div>
          {footer ? <div className="border-t border-border-light px-2 py-1.5">{footer}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function chipClass(selected) {
  return `px-2.5 py-1 rounded-full text-xs font-medium border transition-colors min-h-[32px] ${
    selected
      ? 'bg-primary/10 border-primary text-primary'
      : 'bg-surface border-border-light text-text-secondary hover:border-primary/40'
  }`;
}

function DatePresetChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Date range">
      {MATCHUP_DATE_PRESETS.map((preset) => {
        const selected = value === preset.value;
        return (
          <button
            key={preset.value}
            type="button"
            onClick={() => onChange(preset.value)}
            aria-pressed={selected}
            className={chipClass(selected)}
          >
            {preset.label}
          </button>
        );
      })}
    </div>
  );
}

function GameTypeBucketChips({ selectedValues, onChange }) {
  const selected = new Set(selectedValues);

  const toggle = (value) => {
    if (selected.has(value)) {
      onChange(selectedValues.filter((current) => current !== value));
      return;
    }
    onChange([...selectedValues, value]);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Game type">
        {MATCHUP_GAME_TYPE_BUCKETS.map((bucket) => {
          const isSelected = selected.has(bucket.value);
          return (
            <button
              key={bucket.value}
              type="button"
              onClick={() => toggle(bucket.value)}
              aria-pressed={isSelected}
              className={chipClass(isSelected)}
            >
              {bucket.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="text-xs font-semibold text-primary hover:underline"
          onClick={() => onChange([...DEFAULT_MATCHUP_GAME_TYPE_BUCKETS])}
        >
          Balanced only
        </button>
        <button
          type="button"
          className="text-xs font-semibold text-primary hover:underline"
          onClick={() => onChange([...ALL_MATCHUP_GAME_TYPE_BUCKETS])}
        >
          All types
        </button>
      </div>
    </div>
  );
}

function buildFilterSummary({
  showClubFilter,
  clubs,
  clubIds,
  lockedClubName,
  seasons,
  seasonId,
  gameTypeBuckets,
  datePreset,
  dateFrom,
  dateTo,
}) {
  const parts = [];
  if (showClubFilter && clubIds?.length) {
    if (clubIds.length === 1) {
      const club = clubs.find((c) => c.id === clubIds[0]);
      if (club?.name) parts.push(club.name);
    } else {
      parts.push(`${clubIds.length} clubs`);
    }
  } else if (lockedClubName) {
    parts.push(lockedClubName);
  }
  if (seasonId) {
    const season = seasons.find((s) => s.id === seasonId);
    const seasonLabel = formatSeasonLabel(season);
    if (seasonLabel) parts.push(seasonLabel);
  }
  if (isDefaultBalancedBuckets(gameTypeBuckets)) {
    parts.push('Balanced games');
  } else if (isAllGameTypeBucketsSelected(gameTypeBuckets)) {
    parts.push('All game types');
  } else if (gameTypeBuckets?.length === 1) {
    const label = MATCHUP_GAME_TYPE_BUCKETS.find((bucket) => bucket.value === gameTypeBuckets[0])?.label;
    if (label) parts.push(label);
  } else if (gameTypeBuckets?.length) {
    parts.push(gameTypeBuckets
      .map((value) => MATCHUP_GAME_TYPE_BUCKETS.find((bucket) => bucket.value === value)?.label)
      .filter(Boolean)
      .join(', '));
  }
  if (datePreset && datePreset !== 'all') {
    const presetLabel = MATCHUP_DATE_PRESETS.find((preset) => preset.value === datePreset)?.label;
    if (datePreset === 'custom') {
      if (dateFrom && dateTo) parts.push(`${formatFilterDate(dateFrom)} – ${formatFilterDate(dateTo)}`);
      else if (dateFrom) parts.push(`From ${formatFilterDate(dateFrom)}`);
      else if (dateTo) parts.push(`Until ${formatFilterDate(dateTo)}`);
      else if (presetLabel) parts.push(presetLabel);
    } else if (presetLabel) {
      parts.push(presetLabel);
    }
  }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Collapsible club / season / game type / date filters for Matchups (closed by default).
 */
export function MatchupFilters({
  clubs = [],
  showClubFilter = true,
  clubIds = [],
  onClubIdsChange,
  lockedClubName = null,
  seasons = [],
  seasonId = '',
  onSeasonChange,
  gameTypeBuckets = DEFAULT_MATCHUP_GAME_TYPE_BUCKETS,
  onGameTypeBucketsChange,
  datePreset = 'all',
  onDatePresetChange,
  dateFrom = '',
  onDateFromChange,
  dateTo = '',
  onDateToChange,
  onClearDates,
}) {
  const [expanded, setExpanded] = useState(false);
  const controlClass = 'form-input w-full min-w-0 text-sm py-1.5 sm:py-2';

  const hasDateFilter = datePreset !== 'all' && (datePreset !== 'custom' || Boolean(dateFrom || dateTo));
  const showSeasonFilter = seasons.length > 0 && typeof onSeasonChange === 'function';
  const showClubField = showClubFilter || Boolean(lockedClubName);
  const hasNonDefaultTypes = !isDefaultBalancedBuckets(gameTypeBuckets);
  const hasClubSubset =
    showClubFilter &&
    clubIds?.length > 0 &&
    clubIds.length !== clubs.length;
  const hasActiveFilters =
    hasClubSubset ||
    Boolean(seasonId) ||
    hasNonDefaultTypes ||
    hasDateFilter;

  const summary = useMemo(
    () =>
      buildFilterSummary({
        showClubFilter,
        clubs,
        clubIds,
        lockedClubName,
        seasons,
        seasonId,
        gameTypeBuckets,
        datePreset,
        dateFrom,
        dateTo,
      }),
    [
      showClubFilter,
      clubs,
      clubIds,
      lockedClubName,
      seasons,
      seasonId,
      gameTypeBuckets,
      datePreset,
      dateFrom,
      dateTo,
    ]
  );

  return (
    <div
      className="rounded-xl border border-border-light bg-surface-subtle/60 mb-3 sm:mb-4 overflow-visible"
      aria-label="Matchup filters"
    >
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3 text-left hover:bg-surface-subtle transition-colors rounded-xl"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-text-muted">
            Filters
          </span>
          {!expanded && summary ? (
            <p className="text-xs text-text-secondary truncate mt-0.5">{summary}</p>
          ) : null}
          {!expanded && !summary && hasActiveFilters ? (
            <p className="text-xs text-text-secondary mt-0.5">Active</p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {hasActiveFilters && !expanded ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded">
              On
            </span>
          ) : null}
          <svg
            className={`w-5 h-5 text-text-muted transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded ? (
        <div className="px-3 py-3 sm:px-4 sm:py-4 border-t border-border-light">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-2.5 gap-y-2.5 sm:gap-x-3 sm:gap-y-3 md:grid-cols-3">
              {showClubFilter ? (
                <FilterField id="matchup-filter-club" label="Club" labelShort="Club">
                  <FilterMultiSelect
                    id="matchup-filter-club"
                    options={clubs.map((club) => ({ value: club.id, label: club.name }))}
                    selectedValues={clubIds}
                    onChange={onClubIdsChange}
                    emptyLabel="All clubs"
                    allSelectedLabel="All clubs"
                  />
                </FilterField>
              ) : lockedClubName ? (
                <FilterField id="matchup-filter-club-locked" label="Club" labelShort="Club">
                  <div
                    id="matchup-filter-club-locked"
                    className={`${controlClass} flex items-center font-medium text-text-primary bg-surface-subtle pointer-events-none`}
                    title={lockedClubName}
                  >
                    <span className="truncate">{lockedClubName}</span>
                  </div>
                </FilterField>
              ) : null}

              {showSeasonFilter ? (
                <FilterField id="matchup-filter-season" label="Season" labelShort="Season">
                  <select
                    id="matchup-filter-season"
                    value={seasonId}
                    onChange={(e) => onSeasonChange(e.target.value)}
                    className={controlClass}
                  >
                    <option value="">All seasons</option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>
                        {formatSeasonLabel(season)}
                      </option>
                    ))}
                  </select>
                </FilterField>
              ) : null}

            </div>

            <div>
              <p className="block text-xs font-medium text-text-secondary mb-1.5">Type</p>
              <GameTypeBucketChips
                selectedValues={gameTypeBuckets}
                onChange={onGameTypeBucketsChange}
              />
            </div>

            <div>
              <p className="block text-xs font-medium text-text-secondary mb-1.5">When</p>
              <DatePresetChips value={datePreset} onChange={onDatePresetChange} />
              {datePreset === 'custom' ? (
                <div className="grid grid-cols-2 gap-x-2.5 sm:gap-x-3 md:max-w-xl mt-2.5">
                  <FilterField id="matchup-filter-date-from" label="From" labelShort="From">
                    <input
                      id="matchup-filter-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => onDateFromChange(e.target.value)}
                      className={controlClass}
                    />
                  </FilterField>
                  <FilterField id="matchup-filter-date-to" label="To" labelShort="To">
                    <input
                      id="matchup-filter-date-to"
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={(e) => onDateToChange(e.target.value)}
                      className={controlClass}
                    />
                  </FilterField>
                </div>
              ) : null}
              {hasDateFilter && onClearDates ? (
                <div className="flex justify-end mt-1.5">
                  <button
                    type="button"
                    onClick={onClearDates}
                    className="text-xs font-semibold text-primary hover:underline"
                  >
                    Clear dates
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MatchupFilters;
